// Color-blind-safe palette for hantavirus pathogens.
// Built around the Okabe-Ito 8-color palette + a few neutral extensions, so that any
// two adjacent markers on the map remain distinguishable for protan / deutan / tritan
// vision. Keep these hexes consistent everywhere the UI references a pathogen.
import type { Pathogen, Syndrome, Confidence, OutbreakStatus } from '../types/supabase';

export const PATHOGEN_COLOR: Record<Pathogen, string> = {
  // HPS — Americas (warm half of Okabe-Ito)
  'andes':            '#e69f00', // orange
  'sin-nombre':       '#d55e00', // vermillion
  'choclo':           '#cc79a7', // reddish-purple
  'laguna-negra':     '#f0a868', // warm tan
  'bayou':            '#b86e3c', // burnt sienna
  'black-creek-canal':'#a55b2a',
  // HFRS — Eurasia (cool half of Okabe-Ito)
  'hantaan':          '#0072b2', // blue
  'puumala':          '#56b4e9', // sky blue
  'seoul':            '#009e73', // bluish-green
  'dobrava':          '#3a6f9c',
  'tula':             '#1f7a5e',
  'thailand':         '#4ea3c4',
  // Catch-alls
  'other':            '#a1a1aa',
  'unspecified':      '#52525b',
};

export const PATHOGEN_LABEL: Record<Pathogen, { name: string; abbreviation: string; syndrome: Syndrome }> = {
  'andes':            { name: 'Andes virus',             abbreviation: 'ANDV', syndrome: 'HPS' },
  'sin-nombre':       { name: 'Sin Nombre virus',        abbreviation: 'SNV',  syndrome: 'HPS' },
  'hantaan':          { name: 'Hantaan virus',           abbreviation: 'HTNV', syndrome: 'HFRS' },
  'puumala':          { name: 'Puumala virus',           abbreviation: 'PUUV', syndrome: 'HFRS' },
  'seoul':            { name: 'Seoul virus',             abbreviation: 'SEOV', syndrome: 'HFRS' },
  'dobrava':          { name: 'Dobrava-Belgrade virus',  abbreviation: 'DOBV', syndrome: 'HFRS' },
  'choclo':           { name: 'Choclo virus',            abbreviation: 'CHOV', syndrome: 'HPS' },
  'laguna-negra':     { name: 'Laguna Negra virus',      abbreviation: 'LANV', syndrome: 'HPS' },
  'bayou':            { name: 'Bayou virus',             abbreviation: 'BAYV', syndrome: 'HPS' },
  'black-creek-canal':{ name: 'Black Creek Canal virus', abbreviation: 'BCCV', syndrome: 'HPS' },
  'tula':             { name: 'Tula virus',              abbreviation: 'TULV', syndrome: 'HFRS' },
  'thailand':         { name: 'Thailand virus',          abbreviation: 'THAIV',syndrome: 'HFRS' },
  'other':            { name: 'Other hantavirus',        abbreviation: 'OTHR', syndrome: 'unspecified' },
  'unspecified':      { name: 'Unspecified hantavirus',  abbreviation: 'UNSP', syndrome: 'unspecified' },
};

export const PATHOGEN_ORDER: Pathogen[] = [
  // HPS first
  'andes', 'sin-nombre', 'choclo', 'laguna-negra', 'bayou', 'black-creek-canal',
  // HFRS next
  'hantaan', 'puumala', 'seoul', 'dobrava', 'tula', 'thailand',
  // catch-alls last
  'other', 'unspecified',
];

export const SYNDROME_LABEL: Record<Syndrome, string> = {
  'HPS': 'HPS',
  'HFRS': 'HFRS',
  'unspecified': 'unspec.',
};

export const STATUS_LABEL: Record<OutbreakStatus, string> = {
  active: 'active',
  concluded: 'concluded',
  historical: 'historical',
};

export const CONFIDENCE_CLASSES: Record<Confidence, string> = {
  verified:    'bg-[var(--color-verified-soft)]    text-[var(--color-verified)]    ring-1 ring-[var(--color-verified)]/15',
  preliminary: 'bg-[var(--color-preliminary-soft)] text-[var(--color-preliminary)] ring-1 ring-[var(--color-preliminary)]/15',
  unverified:  'bg-[var(--color-unverified-soft)]  text-[var(--color-unverified)]  ring-1 ring-[var(--color-unverified)]/15',
};

// Marker radius in px from a total case count. Smaller, more precise: sqrt scale,
// clamped to [4, 18]. Bigger circles are noise on a global view; halo + selected
// states do the work of drawing attention instead.
export function markerRadius(totalCases: number): number {
  if (totalCases <= 0) return 8;
  const r = 8 + Math.sqrt(totalCases) * 1.05;
  return Math.max(8, Math.min(18, r));
}
